import { ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF } from './approved-source-systems';
import type {
  ApprovedFormulaDefinition,
  ApprovedFormulaEvidenceArtifact,
  ApprovedFormulaEvidenceValidationBlock,
  ApprovedFormulaImplementationDigestInput,
  ApprovedFormulaResult,
  ApprovedFormulaReconciliationRule,
} from './approved-formulas';

export const ORG_PATTERN_COST_FORMULA_ID = 'org_pattern_tobe_cost_delta_v1';
export const ORG_PATTERN_COST_FORMULA_VERSION = '1.0.0';
export const ORG_PATTERN_COST_CONTEXT_KIND = 'decision_agent:org_pattern_cost';
export const ORG_PATTERN_COST_EVIDENCE_SCHEMA_VERSION =
  'org_pattern_cost_evidence_v1';
const FORMULA_ID = ORG_PATTERN_COST_FORMULA_ID;
const VERSION = ORG_PATTERN_COST_FORMULA_VERSION;
const CONTEXT_KIND = ORG_PATTERN_COST_CONTEXT_KIND;
const FORMULA_RUNTIME_VERSION = 'approved_formula_runtime_v1';
const CANONICALIZER_VERSION = 'approved_formula_canonicalizer_v1';

type FormulaBlocked = Extract<ApprovedFormulaResult<never>, { status: 'blocked' }>;

export type OrgPatternCostRow = Readonly<{
  positionId: string;
  fullyLoadedAnnualCostUsd: number;
  currency: 'USD';
}>;

export type OrgPatternToBeCostDeltaInput = Readonly<{
  baselinePositionIds: readonly string[];
  toBePositionIds: readonly string[];
  baselineCostRows: readonly OrgPatternCostRow[];
  toBeCostRows: readonly OrgPatternCostRow[];
  uncostedNewPositionIds: readonly string[];
  reportingCutAsOf: string;
}>;

export type OrgPatternToBeCostDeltaOutput = Readonly<{
  asIsFullyLoadedCostUsd: number;
  toBeCostedFullyLoadedCostUsd: number;
  costDeltaUsd: number;
  removedPositionCostUsd: number;
  uncostedNewPositionCount: number;
  costedPositionCount: number;
}>;

const definition: ApprovedFormulaDefinition<
  OrgPatternToBeCostDeltaInput,
  OrgPatternToBeCostDeltaOutput
> = Object.freeze({
  formulaId: FORMULA_ID,
  version: VERSION,
  displayName: 'Organization pattern adoption cost delta',
  description:
    'Computes the governed baseline, costed To-Be, removed-position cost, and delta for one organization-pattern state pair while keeping uncosted new roles explicit.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: FORMULA_RUNTIME_VERSION,
  canonicalizerVersion: CANONICALIZER_VERSION,
  inputSchemaVersion: 'org_pattern_tobe_cost_delta_input_v1',
  outputSchemaVersion: 'org_pattern_tobe_cost_delta_output_v1',
  expectedContextKind: CONTEXT_KIND,
  approvedSourceSystemRefs: Object.freeze([
    ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: Object.freeze([
    digestInput('executeOrgPatternToBeCostDelta', executeOrgPatternToBeCostDelta),
    digestInput('validateEvidence', validateEvidence),
    digestInput('stableJson', stableJson),
    digestInput('costMap', costMap),
    digestInput('sum', sum),
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'org_pattern_tobe_cost_delta_v1:costed-and-uncosted-new:v1',
      input: Object.freeze({
        baselinePositionIds: Object.freeze(['p1', 'p2']),
        toBePositionIds: Object.freeze(['p1', 'new-costed', 'new-uncosted']),
        baselineCostRows: Object.freeze([
          Object.freeze({ positionId: 'p1', fullyLoadedAnnualCostUsd: 100, currency: 'USD' }),
          Object.freeze({ positionId: 'p2', fullyLoadedAnnualCostUsd: 200, currency: 'USD' }),
        ]),
        toBeCostRows: Object.freeze([
          Object.freeze({ positionId: 'p1', fullyLoadedAnnualCostUsd: 100, currency: 'USD' }),
          Object.freeze({ positionId: 'new-costed', fullyLoadedAnnualCostUsd: 150, currency: 'USD' }),
        ]),
        uncostedNewPositionIds: Object.freeze(['new-uncosted']),
        reportingCutAsOf: '2026-09-02',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          asIsFullyLoadedCostUsd: 300,
          toBeCostedFullyLoadedCostUsd: 250,
          costDeltaUsd: -50,
          removedPositionCostUsd: 200,
          uncostedNewPositionCount: 1,
          costedPositionCount: 2,
        }),
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateEvidence,
  reconciliationRule: reconciliationRule(),
  execute: executeOrgPatternToBeCostDelta,
});

export const ORG_PATTERN_COST_APPROVED_FORMULA_DEFINITIONS: readonly ApprovedFormulaDefinition[] =
  Object.freeze([definition]);

function executeOrgPatternToBeCostDelta(
  input: OrgPatternToBeCostDeltaInput,
): ApprovedFormulaResult<OrgPatternToBeCostDeltaOutput> {
  const validation = validateInput(input);
  if (validation) return validation;
  const baselineCosts = costMap(input.baselinePositionIds, input.baselineCostRows, true);
  if (baselineCosts.status === 'blocked') return baselineCosts;
  const toBeCosts = costMap(input.toBePositionIds, input.toBeCostRows, false);
  if (toBeCosts.status === 'blocked') return toBeCosts;
  const uncostedIds = new Set(input.uncostedNewPositionIds);
  const uncostedValidation = validateUncosted(
    input,
    baselineCosts.costs,
    toBeCosts.costs,
    uncostedIds,
  );
  if (uncostedValidation) return uncostedValidation;
  const missingToBeCost = input.toBePositionIds.filter(
    (id) => !toBeCosts.costs.has(id) && !uncostedIds.has(id),
  );
  if (missingToBeCost.length > 0) {
    return blocked(
      'missing_formula_input',
      `To-Be cost coverage is incomplete for ${missingToBeCost.join(', ')}.`,
    );
  }
  return ok(output(input, baselineCosts.costs, toBeCosts.costs));
}

function validateInput(
  input: OrgPatternToBeCostDeltaInput,
): ReturnType<typeof blocked> | null {
  if (
    !input ||
    !Array.isArray(input.baselinePositionIds) ||
    !Array.isArray(input.toBePositionIds) ||
    !Array.isArray(input.baselineCostRows) ||
    !Array.isArray(input.toBeCostRows) ||
    !Array.isArray(input.uncostedNewPositionIds) ||
    !nonBlank(input.reportingCutAsOf)
  ) {
    return blocked('missing_formula_input', 'Pattern cost delta requires both state rosters and a reporting cut.');
  }
  if (!uniqueNonBlank(input.baselinePositionIds) || !uniqueNonBlank(input.toBePositionIds)) {
    return blocked('invalid_formula_input', 'Pattern cost delta requires unique non-empty position ids.');
  }
  if (!uniqueNonBlank(input.uncostedNewPositionIds)) {
    return blocked('invalid_formula_input', 'Uncosted new position ids must be unique and non-empty.');
  }
  return input.baselinePositionIds.length > 0
    ? null
    : blocked('missing_formula_input', 'Pattern cost delta requires at least one baseline position.');
}

function costMap(
  positionIds: readonly string[],
  rows: readonly OrgPatternCostRow[],
  complete: boolean,
):
  | Readonly<{ status: 'ok'; costs: ReadonlyMap<string, number> }>
  | FormulaBlocked {
  const ids = new Set(positionIds);
  const costs = new Map<string, number>();
  for (const row of rows) {
    if (!ids.has(row.positionId) || costs.has(row.positionId) || row.currency !== 'USD' || !nonNegative(row.fullyLoadedAnnualCostUsd)) {
      return blocked('invalid_formula_input', 'Pattern cost rows must be unique, in-scope, finite non-negative USD values.');
    }
    costs.set(row.positionId, row.fullyLoadedAnnualCostUsd);
  }
  const missing = positionIds.filter((id) => !costs.has(id));
  if (complete && missing.length > 0) {
    return blocked('missing_formula_input', `Baseline cost coverage is incomplete for ${missing.join(', ')}.`);
  }
  return { status: 'ok', costs };
}

function validateUncosted(
  input: OrgPatternToBeCostDeltaInput,
  baselineCosts: ReadonlyMap<string, number>,
  toBeCosts: ReadonlyMap<string, number>,
  uncostedIds: ReadonlySet<string>,
): ReturnType<typeof blocked> | null {
  const toBeIds = new Set(input.toBePositionIds);
  for (const positionId of uncostedIds) {
    if (!toBeIds.has(positionId) || baselineCosts.has(positionId) || toBeCosts.has(positionId)) {
      return blocked(
        'invalid_formula_input',
        'Uncosted positions must be new To-Be positions with no baseline or To-Be cost row.',
      );
    }
  }
  return null;
}

function output(
  input: OrgPatternToBeCostDeltaInput,
  baselineCosts: ReadonlyMap<string, number>,
  toBeCosts: ReadonlyMap<string, number>,
): OrgPatternToBeCostDeltaOutput {
  const toBeIds = new Set(input.toBePositionIds);
  const asIsFullyLoadedCostUsd = sum(baselineCosts.values());
  const toBeCostedFullyLoadedCostUsd = sum(toBeCosts.values());
  const removedPositionCostUsd = sum(
    input.baselinePositionIds
      .filter((positionId) => !toBeIds.has(positionId))
      .map((positionId) => baselineCosts.get(positionId) as number),
  );
  return {
    asIsFullyLoadedCostUsd,
    toBeCostedFullyLoadedCostUsd,
    costDeltaUsd: roundUsd(toBeCostedFullyLoadedCostUsd - asIsFullyLoadedCostUsd),
    removedPositionCostUsd,
    uncostedNewPositionCount: input.uncostedNewPositionIds.length,
    costedPositionCount: toBeCosts.size,
  };
}

function reconciliationRule(): ApprovedFormulaReconciliationRule<
  OrgPatternToBeCostDeltaInput,
  OrgPatternToBeCostDeltaOutput
> {
  return {
    kind: 'input_output_evidence_reconciliation',
    ruleId: 'org_pattern_tobe_cost_delta_reconciles_to_evidence_v1',
    description: 'Output must equal a deterministic replay of the evidence-bound pattern state pair.',
    validate(input, result, artifacts) {
      const evidenceBlock = validateEvidence(input, artifacts);
      if (evidenceBlock) return evidenceBlock;
      const replay = executeOrgPatternToBeCostDelta(input);
      return replay.status === 'ok' && stableJson(replay.output) === stableJson(result)
        ? null
        : evidenceBlockForReplay();
    },
  };
}

function validateEvidence(
  input: Readonly<OrgPatternToBeCostDeltaInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const matched = artifacts.find((artifact) =>
    artifact.sourceSystemRef === ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF &&
    artifact.contextKind === CONTEXT_KIND &&
    inputFromArtifact(artifact.contentJson) !== null,
  );
  if (!matched) return missingEvidence();
  return stableJson(inputFromArtifact(matched.contentJson)) === stableJson(input)
    ? null
    : { reasonCode: 'formula_reconciliation_failure', message: `${FORMULA_ID} input does not reconcile to its governed state-pair evidence.` };
}

function inputFromArtifact(value: unknown): unknown | null {
  const artifact = record(value);
  if (!artifact) return null;
  const inputs = record(artifact.orgPatternCostFormulaInputs);
  const input = inputs ? inputs[FORMULA_ID] : undefined;
  return input === undefined ? null : input;
}

function digestInput(name: string, implementation: unknown): ApprovedFormulaImplementationDigestInput {
  return Object.freeze({ name, source: String(implementation) });
}

function missingEvidence(): ApprovedFormulaEvidenceValidationBlock {
  return { reasonCode: 'missing_governed_evidence', message: `${FORMULA_ID} requires matching governed state-pair evidence.` };
}

function evidenceBlockForReplay(): ApprovedFormulaEvidenceValidationBlock {
  return { reasonCode: 'formula_reconciliation_failure', message: `${FORMULA_ID} output failed deterministic replay reconciliation.` };
}

function ok(outputValue: OrgPatternToBeCostDeltaOutput): ApprovedFormulaResult<OrgPatternToBeCostDeltaOutput> {
  return { status: 'ok', output: Object.freeze(outputValue) };
}

function blocked(
  reasonCode: 'missing_formula_input' | 'invalid_formula_input',
  message: string,
): FormulaBlocked {
  return { status: 'blocked', reasonCode, message };
}

function uniqueNonBlank(values: readonly string[]): boolean {
  return values.length === new Set(values).size && values.every(nonBlank);
}

function nonBlank(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function nonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function sum(values: Iterable<number>): number {
  let total = 0;
  for (const value of values) total += value;
  return roundUsd(total);
}

function roundUsd(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  const item = record(value);
  if (item) return `{${Object.keys(item).sort().map((key) => `${JSON.stringify(key)}:${stableJson(item[key])}`).join(',')}}`;
  return JSON.stringify(value);
}
